import { Helmet } from 'react-helmet-async';
import { SEOProps } from '../../types';

const SITE_NAME = 'YolSepetiGO Blog';
const DEFAULT_DESCRIPTION = 'Çekici, yol yardım, nakliye ve vinç hizmetleri hakkında bilgi edinin. YolSepetiGO ile yolda kaldığınızda yanınızdayız.';
const SITE_URL = 'https://blog.yolsepetigo.com';

interface Props extends SEOProps {
  noIndex?: boolean;
}

export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = [],
  image,
  url,
  type = 'website',
  publishedTime,
  author,
  noIndex = false,
}: Props) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const fullUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const defaultImage = `${SITE_URL}/goyazililogo.webp`;

  const defaultKeywords = [
    'çekici',
    'yol yardım',
    'nakliye',
    'vinç',
    'araç kurtarma',
    'oto çekici',
    'yolda kaldım',
    'acil yol yardım',
    'YolSepetiGO',
  ];

  const allKeywords = Array.from(new Set([...defaultKeywords, ...keywords]));

  return (
    <Helmet>
      {/* Temel Meta Etiketleri */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords.join(', ')} />
      <link rel="canonical" href={fullUrl} />

      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph Meta Etiketleri */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={image || defaultImage} />
      <meta property="og:locale" content="tr_TR" />

      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}

      {/* Twitter Card Meta Etiketleri */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image || defaultImage} />

      {/* Ek Meta Etiketleri */}
      <meta name="author" content="YolSepetiGO" />
      <meta name="theme-color" content="#14b8aa" />
      <meta name="msapplication-TileColor" content="#14b8aa" />

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': type === 'article' ? 'BlogPosting' : 'WebSite',
          name: fullTitle,
          description: description,
          url: fullUrl,
          ...(type === 'article' && {
            datePublished: publishedTime,
            author: {
              '@type': 'Person',
              name: author,
            },
            publisher: {
              '@type': 'Organization',
              name: SITE_NAME,
              logo: {
                '@type': 'ImageObject',
                url: `${SITE_URL}/goyazililogo.webp`,
              },
            },
            image: image || defaultImage,
          }),
        })}
      </script>
    </Helmet>
  );
}
